import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle, Award, ChevronDown } from "lucide-react";
import { HERO_IMG, waLink, WA_DEFAULT, scrollToId } from "@/data/site";

const EASE = [0.76, 0, 0.24, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden bg-espresso grain">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Premium vegetarian wedding catering buffet by Momay Caterers, Mandvi Kutch"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-espresso via-espresso/55 to-espresso/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-espresso/70 via-transparent to-transparent" />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 min-h-screen flex flex-col justify-end pb-28 md:pb-24 pt-32">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 1 }}
          className="font-script text-3xl md:text-5xl text-gold mb-4"
        >
          The taste you want.
        </motion.p>

        <h1 className="font-serif text-cream leading-[0.95] tracking-tight text-[15vw] md:text-[8.5rem] lg:text-[10rem]" data-testid="hero-headline">
          <MaskedLine delay={0.25}>GOOD FOOD.</MaskedLine>
          <MaskedLine delay={0.45}>GREAT MOMENTS.</MaskedLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-xl"
        >
          <p className="text-cream/90 text-base md:text-lg font-light leading-relaxed" data-testid="hero-subheadline">
            Premium Vegetarian Catering for Celebrations That Deserve Something Special.
          </p>
          <p className="text-cream/60 text-sm md:text-base font-light mt-3 leading-relaxed">
            From intimate family gatherings to grand celebrations, Momay Caterers brings exceptional food,
            thoughtful presentation and professional service to your special occasions.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-quote-btn"
            onClick={() => scrollToId("quote")}
            className="group bg-gold text-espresso px-8 py-4 text-xs md:text-sm tracking-[0.25em] uppercase font-medium hover:bg-cream transition-colors duration-300 flex items-center gap-3"
          >
            Get a Quote
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <a
            data-testid="hero-whatsapp-btn"
            href={waLink(WA_DEFAULT)}
            target="_blank"
            rel="noopener noreferrer"
            className="border border-cream/40 text-cream px-8 py-4 text-xs md:text-sm tracking-[0.25em] uppercase hover:border-gold hover:text-gold transition-colors duration-300 flex items-center gap-3"
          >
            <MessageCircle size={16} />
            WhatsApp Us
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.7, duration: 1 }}
          className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2"
        >
          <span data-testid="hero-trust-badge" className="inline-flex items-center gap-2 text-gold text-xs tracking-[0.3em] uppercase">
            <Award size={14} /> 25+ Years of Experience
          </span>
          <span className="text-cream/50 text-xs tracking-[0.2em] uppercase">
            Pure Vegetarian &nbsp;•&nbsp; Jain Food Available &nbsp;•&nbsp; Professional Catering
          </span>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("trust")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-cream/50 hover:text-gold transition-colors hidden md:block"
        aria-label="Scroll down"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }} className="block">
          <ChevronDown size={22} />
        </motion.span>
      </motion.button>
    </section>
  );
};
